import { useCallback, useEffect } from 'react'
import type { ShiftManagementShiftInteractorDropActionEnum } from 'generated/resources/graphql'

import {
  getColumnCellCoords,
  getRowCellCoords,
} from 'modules/shift_management/utils/TimeGridTable/cellCoordinates'
import { useClipboardShortcuts } from 'modules/shift_management/hooks/gridTable/useClipboardShortcuts'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import {
  useClipboardDispatch,
  useClipboardState,
} from 'modules/shift_management/hooks/useClipboard'
import type { ClipboardScopeKind } from 'modules/shift_management/hooks/reducers/clipboard'
import type {
  ClipboardHandlers,
  ClipboardSelection,
  GridSelection,
  Row,
  TimeGridCellContext,
  TimeGridEntity,
  TimeGridEvent,
  TimeSlot,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'

const serializeSelection = (selection: GridSelection, slots: TimeSlot[]): string => {
  const firstSlot = slots[0]
  const lastSlot = slots[slots.length - 1]
  const rangeSnapshot = `${firstSlot?.start.toISOString() || 'empty'}:${lastSlot?.end.toISOString() || 'empty'}`
  if (selection.kind === 'all') return 'all'
  if (selection.kind === 'none') return `none:${rangeSnapshot}`
  if (selection.kind === 'row') return `row:${selection.rowId}:${rangeSnapshot}`
  if (selection.kind === 'rowRange') {
    return `rowRange:${selection.startIndex}:${selection.endIndex}:${rangeSnapshot}`
  }
  if (selection.kind === 'column') return `column:${selection.columnIndex}:${rangeSnapshot}`
  if (selection.kind === 'columnRange') {
    return `columnRange:${selection.startIndex}:${selection.endIndex}:${rangeSnapshot}`
  }
  if (selection.kind === 'cells') {
    const sortedKeys = Array.from(selection.keys).sort().join(',')
    return [
      'cells',
      rangeSnapshot,
      selection.anchor.entityId,
      selection.anchor.slotIndex,
      sortedKeys,
    ].join(':')
  }

  return selection.kind === 'event'
    ? `event:${rangeSnapshot}:${Array.from(selection.keys).sort().join(',')}`
    : `unknown:${rangeSnapshot}`
}

interface GridLayout<TEntity extends TimeGridEntity, TEvent extends TimeGridEvent> {
  rows: Row<TEntity, TEvent>[]
  slots: TimeSlot[]
  rowByEntityId: Map<string, Row<TEntity, TEvent>>
  rowIndexByEntityId: Map<string, number>
}

interface GridSelectionInput {
  selection: GridSelection
  fromCellKey: (key: string) => { entityId: string; slotIndex: number }
}

interface UseGridClipboardProps<TEntity extends TimeGridEntity, TEvent extends TimeGridEvent> {
  layout: GridLayout<TEntity, TEvent>
  selection: GridSelectionInput
  clipboardHandlers: ClipboardHandlers<TEntity, TEvent> | undefined
  clearSelection: () => void
  onDelete?: (event: KeyboardEvent) => void
  onCopyReady?: (onCopy: () => void) => void
}

function useGridClipboard<TEntity extends TimeGridEntity, TEvent extends TimeGridEvent>({
  layout,
  selection,
  clipboardHandlers,
  clearSelection,
  onDelete,
  onCopyReady,
}: UseGridClipboardProps<TEntity, TEvent>): void {
  const { rows, slots, rowByEntityId, rowIndexByEntityId } = layout
  const { selection: selectionState, fromCellKey } = selection
  const { clipboard } = useClipboardState()
  const dispatchClipboard = useClipboardDispatch()
  const [{ hasMore }] = usePagination()

  const getTopLeftSelectedCell = useCallback(() => {
    if (selectionState.kind !== 'cells' || selectionState.keys.size === 0) return null

    let topLeftCell: { entityId: string; slotIndex: number } | null = null
    let topLeftRowIndex: number | null = null

    selectionState.keys.forEach((key) => {
      const cell = fromCellKey(key)
      const rowIndex = rowIndexByEntityId.get(cell.entityId)
      if (rowIndex === undefined) return

      if (
        topLeftCell === null ||
        topLeftRowIndex === null ||
        rowIndex < topLeftRowIndex ||
        (rowIndex === topLeftRowIndex && cell.slotIndex < topLeftCell.slotIndex)
      ) {
        topLeftCell = cell
        topLeftRowIndex = rowIndex
      }
    })
    return topLeftCell
  }, [fromCellKey, rowIndexByEntityId, selectionState])

  const buildCellContext = useCallback(
    (cell: {
      entityId: string
      slotIndex: number
    }): TimeGridCellContext<TEntity, TEvent> | null => {
      const slot = slots[cell.slotIndex]
      if (!slot) return null

      const row = rowByEntityId.get(cell.entityId)
      if (!row) return null

      const cellEvents = row.layouts
        .filter(
          (layoutRow) =>
            layoutRow.span.startIndex <= cell.slotIndex && layoutRow.span.endIndex >= cell.slotIndex
        )
        .map((layoutRow) => layoutRow.event)

      return {
        entity: row.entity,
        slot,
        events: cellEvents,
      }
    },
    [rowByEntityId, slots]
  )

  const buildClipboardSelection = useCallback(
    (
      cells: Array<{ entityId: string; slotIndex: number }>,
      anchorCell: { entityId: string; slotIndex: number }
    ): ClipboardSelection<TEntity, TEvent> | null => {
      const anchorContext = buildCellContext(anchorCell)
      const anchorRowIndex = rowIndexByEntityId.get(anchorCell.entityId)
      if (!anchorContext || anchorRowIndex === undefined) return null

      const resolvedCells = cells
        .map((cell) => {
          const context = buildCellContext(cell)
          const rowIndex = rowIndexByEntityId.get(cell.entityId)
          if (!context || rowIndex === undefined) return null

          return {
            context,
            rowOffset: rowIndex - anchorRowIndex,
            columnOffset: cell.slotIndex - anchorCell.slotIndex,
          }
        })
        .filter((cell): cell is NonNullable<typeof cell> => cell !== null)
        .sort((left, right) => {
          if (left.rowOffset !== right.rowOffset) return left.rowOffset - right.rowOffset
          return left.columnOffset - right.columnOffset
        })

      if (resolvedCells.length === 0) return null

      return {
        anchor: anchorContext,
        cells: resolvedCells,
      }
    },
    [buildCellContext, rowIndexByEntityId]
  )

  const buildEventSelection = useCallback(
    (eventKeys: Set<string>): ClipboardSelection<TEntity, TEvent> | null => {
      const parsedKeys = Array.from(eventKeys)
        .map((key) => {
          const separatorIndex = key.lastIndexOf(':')
          if (separatorIndex <= 0 || separatorIndex >= key.length - 1) return null
          return {
            entityId: key.slice(0, separatorIndex),
            eventId: key.slice(separatorIndex + 1),
          }
        })
        .filter(
          (
            parsed
          ): parsed is {
            entityId: string
            eventId: string
          } => parsed !== null
        )

      const resolvedEvents = parsedKeys
        .map((parsed) => {
          const row = rowByEntityId.get(parsed.entityId)
          if (!row) return null

          const rowIndex = rowIndexByEntityId.get(parsed.entityId)
          if (rowIndex === undefined) return null

          const eventLayout = row.layoutById?.get(parsed.eventId)
          if (!eventLayout) return null

          const slot = slots[eventLayout.span.startIndex]
          if (!slot) return null

          return {
            row,
            rowIndex,
            layout: eventLayout,
            slot,
          }
        })
        .filter((event): event is NonNullable<typeof event> => event !== null)
        .sort((left, right) => {
          if (left.rowIndex !== right.rowIndex) return left.rowIndex - right.rowIndex
          if (left.layout.span.startIndex !== right.layout.span.startIndex) {
            return left.layout.span.startIndex - right.layout.span.startIndex
          }
          return left.layout.event.id.localeCompare(right.layout.event.id)
        })

      if (resolvedEvents.length === 0) return null
      const anchorEvent = resolvedEvents[0]
      if (!anchorEvent) return null

      const anchorContext: TimeGridCellContext<TEntity, TEvent> = {
        entity: anchorEvent.row.entity,
        slot: anchorEvent.slot,
        events: [anchorEvent.layout.event],
      }

      const cells = resolvedEvents.map((event) => {
        const context: TimeGridCellContext<TEntity, TEvent> = {
          entity: event.row.entity,
          slot: event.slot,
          events: [event.layout.event],
        }

        return {
          context,
          rowOffset: event.rowIndex - anchorEvent.rowIndex,
          columnOffset: event.layout.span.startIndex - anchorEvent.layout.span.startIndex,
        }
      })

      return {
        anchor: anchorContext,
        cells,
      }
    },
    [rowByEntityId, rowIndexByEntityId, slots]
  )

  const buildRowSelection = useCallback(
    (rowId: string): ClipboardSelection<TEntity, TEvent> | null => {
      const anchorCell = { entityId: rowId, slotIndex: 0 }
      const cells = getRowCellCoords(rowId, slots.length)
      return buildClipboardSelection(cells, anchorCell)
    },
    [buildClipboardSelection, slots.length]
  )

  const buildRowRangeSelection = useCallback(
    (startIndex: number, endIndex: number): ClipboardSelection<TEntity, TEvent> | null => {
      const anchorRow = rows[startIndex]
      if (!anchorRow) return null

      const anchorCell = { entityId: anchorRow.entity.id, slotIndex: 0 }
      const cells = rows.slice(startIndex, endIndex + 1).flatMap((row) => {
        return getRowCellCoords(row.entity.id, slots.length)
      })

      return buildClipboardSelection(cells, anchorCell)
    },
    [buildClipboardSelection, rows, slots.length]
  )

  const buildColumnSelection = useCallback(
    (columnIndex: number): ClipboardSelection<TEntity, TEvent> | null => {
      const firstRow = rows[0]
      if (!firstRow) return null
      const anchorCell = { entityId: firstRow.entity.id, slotIndex: columnIndex }
      const cells = getColumnCellCoords(columnIndex, rows)
      return buildClipboardSelection(cells, anchorCell)
    },
    [buildClipboardSelection, rows]
  )

  const buildColumnRangeSelection = useCallback(
    (startIndex: number, endIndex: number): ClipboardSelection<TEntity, TEvent> | null => {
      const firstRow = rows[0]
      if (!firstRow) return null

      const anchorCell = { entityId: firstRow.entity.id, slotIndex: startIndex }
      const cells = rows.flatMap((row) => {
        const rowCells: Array<{ entityId: string; slotIndex: number }> = []

        for (let columnIndex = startIndex; columnIndex <= endIndex; columnIndex += 1) {
          rowCells.push({ entityId: row.entity.id, slotIndex: columnIndex })
        }

        return rowCells
      })

      return buildClipboardSelection(cells, anchorCell)
    },
    [buildClipboardSelection, rows]
  )

  const handleCopyOrCut = useCallback(
    (mode: ShiftManagementShiftInteractorDropActionEnum) => {
      if (!clipboardHandlers) return

      const hasEventSelection = selectionState.kind === 'event'
      const hasRowSelection = selectionState.kind === 'row'
      const hasRowRangeSelection = selectionState.kind === 'rowRange'
      const hasColumnSelection = selectionState.kind === 'column'
      const hasAllSelection = selectionState.kind === 'all'
      const hasCellsSelection = selectionState.kind === 'cells' && selectionState.keys.size > 0
      const hasColumnRangeSelection = selectionState.kind === 'columnRange'

      if (
        !hasEventSelection &&
        !hasRowSelection &&
        !hasRowRangeSelection &&
        !hasColumnSelection &&
        !hasColumnRangeSelection &&
        !hasAllSelection &&
        !hasCellsSelection
      ) {
        return
      }

      if (hasMore && (hasColumnSelection || hasAllSelection)) {
        return
      }

      let sourceSelection: ClipboardSelection<TEntity, TEvent> | null = null
      let scopeKind: ClipboardScopeKind = 'cells'

      if (hasEventSelection) {
        sourceSelection = buildEventSelection(selectionState.keys)
        scopeKind = 'events'
      } else if (hasRowSelection) {
        sourceSelection = buildRowSelection(selectionState.rowId)
        scopeKind = 'row'
      } else if (hasRowRangeSelection) {
        sourceSelection = buildRowRangeSelection(selectionState.startIndex, selectionState.endIndex)
        scopeKind = 'rowRange'
      } else if (hasColumnSelection) {
        sourceSelection = buildColumnSelection(selectionState.columnIndex)
        scopeKind = 'column'
      } else if (hasColumnRangeSelection) {
        sourceSelection = buildColumnRangeSelection(
          selectionState.startIndex,
          selectionState.endIndex
        )
        scopeKind = 'columnRange'
      } else if (hasCellsSelection) {
        const sourceCells = Array.from(selectionState.keys).map(fromCellKey)
        const sourceReferenceCell = getTopLeftSelectedCell() ?? selectionState.anchor
        sourceSelection = buildClipboardSelection(sourceCells, sourceReferenceCell)
      }

      if (!sourceSelection) return

      const payload = clipboardHandlers.getCopyPayload(sourceSelection)
      if (payload === null) return

      dispatchClipboard({
        type: 'SET_CLIPBOARD',
        clipboard: {
          source: sourceSelection,
          payload,
          mode,
          scopeKind,
          selectionSnapshot: serializeSelection(selectionState, slots),
          showSourceFeedback: true,
        },
      })
    },
    [
      buildEventSelection,
      buildClipboardSelection,
      buildRowRangeSelection,
      buildColumnSelection,
      buildColumnRangeSelection,
      buildRowSelection,
      clipboardHandlers,
      dispatchClipboard,
      fromCellKey,
      getTopLeftSelectedCell,
      hasMore,
      selectionState,
      slots,
    ]
  )

  const handlePaste = useCallback(async () => {
    const copied = clipboard
    if (!copied || !clipboardHandlers) return
    if (copied.selectionSnapshot === serializeSelection(selectionState, slots)) return

    const buildTargetSelectionFromTopLeftCell = (targetReferenceCell: {
      entityId: string
      slotIndex: number
    }): ClipboardSelection<TEntity, TEvent> | null => {
      const targetAnchorRowIndex = rowIndexByEntityId.get(targetReferenceCell.entityId)
      if (targetAnchorRowIndex === undefined) return null

      const targetCells = copied.payload
        .map((entry) => {
          const targetRow = rows[targetAnchorRowIndex + entry.rowOffset]
          if (!targetRow) return null

          const targetSlotIndex = targetReferenceCell.slotIndex + entry.columnOffset
          if (targetSlotIndex < 0 || targetSlotIndex >= slots.length) return null

          return {
            entityId: targetRow.entity.id,
            slotIndex: targetSlotIndex,
          }
        })
        .filter((cell): cell is NonNullable<typeof cell> => cell !== null)

      return buildClipboardSelection(targetCells, targetReferenceCell)
    }

    let targetSelection: ClipboardSelection<TEntity, TEvent> | null = null
    if (copied.scopeKind === 'events') {
      if (selectionState.kind === 'event' && selectionState.keys.size > 0) {
        targetSelection = buildEventSelection(selectionState.keys)
      } else {
        const targetReferenceCell = getTopLeftSelectedCell()
        if (!targetReferenceCell) return
        targetSelection = buildTargetSelectionFromTopLeftCell(targetReferenceCell)
      }
    } else if (copied.scopeKind === 'row' || copied.scopeKind === 'rowRange') {
      if (selectionState.kind === 'row') {
        const copiedRowCount =
          copied.scopeKind === 'rowRange'
            ? copied.source.cells.reduce(
                (maxHeight, cell) => Math.max(maxHeight, cell.rowOffset),
                0
              ) + 1
            : 1
        const targetStartIndex = rowIndexByEntityId.get(selectionState.rowId)
        if (targetStartIndex === undefined) return

        targetSelection = buildRowRangeSelection(
          targetStartIndex,
          targetStartIndex + copiedRowCount - 1
        )
      } else if (selectionState.kind === 'rowRange') {
        targetSelection = buildRowRangeSelection(selectionState.startIndex, selectionState.endIndex)
      } else {
        return
      }
    } else if (copied.scopeKind === 'column' || copied.scopeKind === 'columnRange') {
      if (selectionState.kind === 'column') {
        const copiedColumnCount =
          copied.scopeKind === 'columnRange'
            ? copied.source.cells.reduce(
                (maxWidth, cell) => Math.max(maxWidth, cell.columnOffset),
                0
              ) + 1
            : 1

        targetSelection = buildColumnRangeSelection(
          selectionState.columnIndex,
          selectionState.columnIndex + copiedColumnCount - 1
        )
      } else if (selectionState.kind === 'columnRange') {
        targetSelection = buildColumnRangeSelection(
          selectionState.startIndex,
          selectionState.endIndex
        )
      } else {
        return
      }
    } else {
      const targetReferenceCell = getTopLeftSelectedCell()
      if (!targetReferenceCell) return
      targetSelection = buildTargetSelectionFromTopLeftCell(targetReferenceCell)
    }

    if (!targetSelection) return

    const canPaste = clipboardHandlers.canPaste?.({
      source: copied.source,
      target: targetSelection,
      payload: copied.payload,
    })

    if (canPaste === false) return
    await clipboardHandlers.onPaste({
      source: copied.source,
      target: targetSelection,
      payload: copied.payload,
      mode: copied.mode,
      replaceTargetContent: copied.scopeKind !== 'events',
    })

    dispatchClipboard({ type: 'HIDE_CLIPBOARD_SOURCE_FEEDBACK' })
  }, [
    buildEventSelection,
    buildClipboardSelection,
    buildRowRangeSelection,
    buildColumnRangeSelection,
    buildRowSelection,
    clipboard,
    clipboardHandlers,
    getTopLeftSelectedCell,
    dispatchClipboard,
    rowIndexByEntityId,
    rows,
    selectionState,
    slots,
  ])

  const handleEscape = useCallback(() => {
    clearSelection()
  }, [clearSelection])

  const handleCopy = useCallback(() => {
    handleCopyOrCut('copy')
  }, [handleCopyOrCut])

  const handleCut = useCallback(() => {
    handleCopyOrCut('move')
  }, [handleCopyOrCut])

  useClipboardShortcuts({
    onCopy: clipboardHandlers ? handleCopy : undefined,
    onCut: clipboardHandlers ? handleCut : undefined,
    onPaste: clipboardHandlers ? handlePaste : undefined,
    onDelete,
    onEscape: handleEscape,
  })

  useEffect(() => {
    if (!onCopyReady) return
    onCopyReady(handleCopy)
  }, [handleCopy, onCopyReady])
}

export { useGridClipboard }
